import { useQuery } from "@tanstack/react-query";
import { useAuth } from "hooks/useAuth";
import { dashboardService } from "services/dashboard.service";
import { queryClient } from "lib/queryClient";
import { queryKeys } from "lib/queryKeys";
import { isRegistrationPage } from "utils/publicApi";
import { isTenantRole } from "utils/roles";
import { AdminDashboard, CompanyDashboard } from "types/dashboard";
import type {
  ScanAnalyticsPeriod,
  ScanAnalyticsScope,
  RecentScansFilters,
} from "types/scanAnalytics";

const DASHBOARD_QUERY_PREFIXES = [
  ["license", "dashboard"],
  queryKeys.dashboard.admin,
  ["dashboard", "scanAnalytics"],
] as const;

export function refreshDashboardQueries() {
  return Promise.all(
    DASHBOARD_QUERY_PREFIXES.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    )
  );
}

export function useAccountDashboardQuery(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.license.dashboard("account"),
    queryFn: () => dashboardService.getAccountDashboard(),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function useAdminDashboardQuery(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.dashboard.admin,
    queryFn: () => dashboardService.getAdminDashboard(),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function useScanAnalyticsQuery(
  period: ScanAnalyticsPeriod,
  enabled: boolean,
  scope?: ScanAnalyticsScope
) {
  return useQuery({
    queryKey: queryKeys.dashboard.scanAnalytics(period, scope),
    queryFn: () => dashboardService.getScanAnalytics(period, scope),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

const RECENT_SCANS_PAGE_SIZE = 10;

export function useRecentScansQuery(
  enabled: boolean,
  filters: RecentScansFilters,
  page: number,
  scope?: ScanAnalyticsScope
) {
  return useQuery({
    queryKey: queryKeys.dashboard.recentScans(
      scope,
      page,
      RECENT_SCANS_PAGE_SIZE,
      filters
    ),
    queryFn: () =>
      dashboardService.getRecentScans({
        page,
        pageSize: RECENT_SCANS_PAGE_SIZE,
        from: filters.from,
        to: filters.to,
        employeeId: filters.employeeId,
        scope,
      }),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export { RECENT_SCANS_PAGE_SIZE };

export function useLicenseStatusQuery() {
  const { user } = useAuth();
  const isTenantUser = isTenantRole(user?.roleId);
  const enabled = isTenantUser && !isRegistrationPage();

  const query = useAccountDashboardQuery(enabled);

  const data = query.data ?? null;
  const licenseExists = data?.metrics?.hasLicense ?? false;
  const expired = data?.metrics?.isLicenseExpired ?? false;
  const days = data?.metrics?.remainingDaysToExpire ?? 0;

  return {
    ...query,
    isTenantUser,
    companyData: data as CompanyDashboard | null,
    isExpired: expired,
    hasLicense: licenseExists,
    remainingDays: days,
  };
}

export type { AdminDashboard, CompanyDashboard };
